/**
 * @module view/Devices/Detail
 */

import './style.css';
import {
  Link,
  useLocation,
  useNavigate,
  useParams
} from 'react-router-dom';
import {
  createElement,
  useEffect,
  useState
} from 'react';
import { getAllCircuits, getConfigItem, isDuo } from 'util/device';
import useDevicesStore from 'store/devices';
import DefaultView from './components/default-view';
import SiteView from './components/site-view';
import LocationView from './components/location-view';
import GatewayView from './components/gateway-view';
import BridgeView from './components/bridge-view';
import PanelView from './components/panel-view';
import GenesisPanelView from './components/genesis-panel-view';
import ControllerView from './components/controller-view';
import CircuitView from './components/circuit-view';
import DuoView from './components/duo-view';
import TagView from './components/tag-view';
import { LoadingIndicator } from '@thermon-platforms/thermon-ui-core';
import useSessionStore from 'store/session';
import { last } from 'lodash';


/**
  * @function getUrlFromBreadcrumbs
  * @memberof module:view/Devices/Detail
  * @param {number} i depth of device
  * @returns {string} path for device route
  */
function getUrlFromBreadcrumbs(breadcrumbs, i) {
  if (!breadcrumbs?.length) return '';
  return breadcrumbs.slice(0, i)
    .reduce((acc, crumb) => `${acc}/${crumb[1]}`, '/devices')
}

/**
 * @function Detail
 * @memberof module:view/Devices/Detail
 * @returns {ReactElement}
 */
function Detail() {
  const navigate = useNavigate();
  const [devicesStore] = useDevicesStore();
  const [device, setDevice] = useState();
  const location = useLocation();
  const [, sessionStoreActions] = useSessionStore();
  const params = useParams();
  const [breadcrumbs, setBreadcrumbs] = useState([]);

  useEffect(() => {
    if (!location || !params) return;
    // React Router doesn't provide an ordered list of route
    // components (params) so let's compile our breadcrumbs
    // from the actual url
    const path = location?.pathname?.split('/').slice(2, 999);
    const crumbs = [];
    const site = devicesStore.devices.find(d => d.type == 'Site');
    sessionStoreActions.setRecentDevice(path);

    // Handle virtual tag devices
    if (location?.pathname?.startsWith('/devices/tag-')) {
      const circuits = getAllCircuits(site);
      // Extract the tag name from the URL
      const id = location?.pathname.split('tag-')[1];
      // and decode any encoded characters
      const name = decodeURI(id);
      // Create a virtual device for the tag
      setDevice({
        model: {
          id,
          name,
          type: 'Tag',
          children: (circuits || []).filter(
            circuit => {
              return getConfigItem(circuit, 'ProcessTag')?.value == name ||
              circuit?.tags?.includes(name)
            }
          )
        }
      });
      // Add our site to the breadcrumbs
      crumbs.push([site.name, site.id]);
      // Add the virtual tag device to crumbs
      crumbs.push([name, id]);
    } else {
      const childOfDuo = path.some(p => isDuo(devicesStore.shallowDevices.get(p)?.model?.externalId));
      path.forEach((p) => {
        if (childOfDuo) {
          const device = devicesStore.shallowDevices.get(p);
          if (device) {
            if (device.model.type == 'Panel' || device.model.type == 'Controller') return;
          }
        }
        for (let key in params) {
          if (params[key] == p) {
            crumbs.push([key, p])
          }
        }
      });
      const deviceId = last(path);
      const device = devicesStore.shallowDevices.get(deviceId);
      // If we can't find the device, redirect to site view
      if (device) {
        setDevice(device);
      } else {
        navigate(`/devices/${site.id}`);
      }
    }
    setBreadcrumbs(crumbs);
  }, [location, params]);

  /**
   * @function selectSubView
   * @param {object} device device model
   * @returns {React.ReactElement} custom view for device details
   */
  function selectSubView(device) {
    const type = isDuo(device?.model?.externalId) ? 'Duo' : device?.model?.type || 'default';
    const model = device?.model?.model;
    const views = {
      Circuit: CircuitView,
      Panel: model == 'Genesis Panel' ? GenesisPanelView : PanelView,
      Controller: ControllerView,
      Duo: DuoView,
      Bridge: BridgeView,
      VirtualBridge: BridgeView, // NOTE - This will likely be obsolete soon
      Gateway: GatewayView,
      Location: LocationView,
      Site: SiteView,
      Tag: TagView
    }

    return createElement((views[type] || DefaultView), {
      device,
      key: `${type}-view-${device.model.id}`
    });
  }

  if (!device) return (<LoadingIndicator />);

  return (
    <div className={`view device-detail ${isDuo(device?.model?.externalId) ? 'Duo' : device.model.type}`}>
      <ul className="breadcrumbs">
        {/* <li><Link to="/devices"><Trans>Sites</Trans></Link></li> */}
        {device ? breadcrumbs.map((crumb, i) => {
          const device = devicesStore.shallowDevices.get(crumb[1]);
          return (<li key={`crumb-${crumb[0]}`}>
            <Link to={getUrlFromBreadcrumbs(breadcrumbs, i + 1)}>
              <span className={`device-icon ${(isDuo(device?.model?.externalId) && !device?.model?.name.includes('CKT')) ? 'duo' : device?.model?.type?.toLowerCase() || 'tag'}`}></span>
              {device?.model?.name || crumb[0]}
            </Link>
          </li>);
        }) : <></>}
      </ul>

      {selectSubView(device)}
    </div>
  )
}

export default Detail;
